import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PageModel } from 'client/data/models/page';
import { XHeaders } from 'client/data/models/x-headers';
import { VenomVersion } from 'client/data/models/version';
import { getMetricsPageHeaders } from 'client/utils/metrics-header';
import { RequestModel } from 'client/data/models/request';

/**
 * Using special prop names in order to avoid possible prop collision.
 */
const stateToPropsConfig = {
  __metricsPageName: bindToPath('page.name', PageModel),
  __metricsPageCategory: bindToPath('page.category', PageModel),
  __metricsXTraceId: bindToPath('x-trace-id', XHeaders),
  __metricsVenomVersion: bindToPath('version', VenomVersion),
  __protocol: bindToPath('protocol', RequestModel),
  __hostName: bindToPath('hostName', RequestModel),
  __url: bindToPath('url', RequestModel),
};

/**
 * HOC (High Order Component) to pass `apiMetrics` prop into child components.
 *
 * This function helps to pass the metrics into API calls that cannot use the `withMetrics` function from the
 * `client/data/api/api-metrics.js` file.
 * @param WrappedComponent child component that is wrapped by additional helper components.
 * @param includeRequestMetrics flag to include protocol,hostName and url params
 * @returns React component to render `WrappedComponent` with `apiMetrics` prop.
 */
export const withMetrics = (WrappedComponent, includeRequestMetrics = false) =>
  connectToModel(
    ({
      __metricsPageName,
      __metricsPageCategory,
      __metricsXTraceId,
      __metricsVenomVersion,
      __protocol,
      __hostName,
      __url,
      ...otherProps
    }) => {
      const metrics = useMemo(
        () => ({
          pageName: __metricsPageName,
          pageCategory: __metricsPageCategory,
          xTraceId: __metricsXTraceId,
          venomVersion: __metricsVenomVersion,
          ...(includeRequestMetrics ? { protocol: __protocol, hostName: __hostName, url: __url } : {}),
        }),
        [
          __hostName,
          __metricsPageCategory,
          __metricsPageName,
          __metricsVenomVersion,
          __metricsXTraceId,
          __protocol,
          __url,
        ]
      );
      return <WrappedComponent {...otherProps} apiMetrics={metrics} />;
    },
    stateToPropsConfig
  );

export const ApiMetrics = PropTypes.shape({
  pageName: PropTypes.string.isRequired,
  pageCategory: PropTypes.string.isRequired,
  xTraceId: PropTypes.string.isRequired,
  venomVersion: PropTypes.string.isRequired,
  protocol: PropTypes.string,
  hostName: PropTypes.string,
  url: PropTypes.string,
});

export const getMetricApiOptions = apiMetrics => ({
  headers: getMetricsPageHeaders(apiMetrics || {}),
});
